import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #000000 100%);
  padding: 2rem;
  font-family: 'Orbitron', monospace;
`;

const DashboardHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const DashboardTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  color: #00ffff;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  margin-bottom: 1rem;
`;

const DashboardSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
  color: #cccccc;
  max-width: 600px;
  margin: 0 auto;
`;

const QuestionsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const QuestionCard = styled(motion.div)`
  background: rgba(0, 17, 34, 0.8);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  position: relative;
  
  &:hover {
    border-color: #00ffff;
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.2);
  }
`;

const QuestionText = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
  color: #ffffff;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const QuestionMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #888888;
`;

const Timestamp = styled.span`
  color: #00ffff;
`;

const Status = styled.span`
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.$status === 'pending' ? 'rgba(255, 165, 0, 0.2)' : 'rgba(0, 255, 0, 0.2)'};
  color: ${props => props.$status === 'pending' ? '#ffa500' : '#00ff00'};
  border: 1px solid ${props => props.$status === 'pending' ? '#ffa500' : '#00ff00'};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &.resolve {
    background: #00ff00;
    color: #000000;
    
    &:hover {
      background: #00cc00;
    }
  }
  
  &.delete {
    background: #ff0000;
    color: #ffffff;
    
    &:hover {
      background: #cc0000;
    }
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #00ffff;
  font-size: 1.2rem;
  margin: 2rem 0;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #888888;
  font-size: 1.1rem;
  margin: 2rem 0;
`;

const UnansweredQuestionsDashboard = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUnansweredQuestions();
  }, []);

  const fetchUnansweredQuestions = async () => {
    try {
      setLoading(true);
      const unansweredCollection = collection(db, 'unansweredQuestions');
      const q = query(unansweredCollection, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const questionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setQuestions(questionsData);
    } catch (error) {
      console.error('Error fetching unanswered questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsResolved = async (questionId) => {
    try {
      const questionRef = doc(db, 'unansweredQuestions', questionId);
      await updateDoc(questionRef, {
        status: 'resolved',
        resolvedAt: new Date()
      });
      
      // Update local state
      setQuestions(prev => prev.map(q => 
        q.id === questionId ? { ...q, status: 'resolved' } : q
      ));
    } catch (error) {
      console.error('Error marking question as resolved:', error);
    }
  };

  const deleteQuestion = async (questionId) => {
    try {
      const questionRef = doc(db, 'unansweredQuestions', questionId);
      await deleteDoc(questionRef);
      
      // Update local state
      setQuestions(prev => prev.filter(q => q.id !== questionId));
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingMessage>Loading unanswered questions...</LoadingMessage>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <DashboardTitle>Unanswered Questions Dashboard</DashboardTitle>
        <DashboardSubtitle>
          Monitor and manage questions that couldn't be answered by the chatbot
        </DashboardSubtitle>
      </DashboardHeader>

      <QuestionsGrid>
        {questions.length === 0 ? (
          <EmptyMessage>
            No unanswered questions found. Great job keeping the chatbot up to date!
          </EmptyMessage>
        ) : (
          questions.map((question, index) => (
            <QuestionCard
              key={question.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <QuestionText>{question.question}</QuestionText>
              
              <QuestionMeta>
                <Timestamp>Asked: {formatTimestamp(question.timestamp)}</Timestamp>
                <Status $status={question.status}>
                  {question.status}
                </Status>
              </QuestionMeta>
              
              {question.userAgent && (
                <div style={{ fontSize: '0.8rem', color: '#666666', marginBottom: '0.5rem' }}>
                  User Agent: {question.userAgent.substring(0, 100)}...
                </div>
              )}
              
              {question.pageUrl && (
                <div style={{ fontSize: '0.8rem', color: '#666666', marginBottom: '1rem' }}>
                  Page: {question.pageUrl}
                </div>
              )}
              
              <ActionButtons>
                <ActionButton 
                  className="resolve"
                  onClick={() => markAsResolved(question.id)}
                  disabled={question.status === 'resolved'}
                >
                  {question.status === 'resolved' ? 'Resolved' : 'Mark as Resolved'}
                </ActionButton>
                <ActionButton 
                  className="delete"
                  onClick={() => deleteQuestion(question.id)}
                >
                  Delete
                </ActionButton>
              </ActionButtons>
            </QuestionCard>
          ))
        )}
      </QuestionsGrid>
    </DashboardContainer>
  );
};

export default UnansweredQuestionsDashboard; 